import { useQuery } from "@tanstack/react-query";
import { addDays, addYears, formatISO, startOfDay, startOfYear } from "date-fns";
import { ArrowRight, Boxes, CircleDollarSign, ListChecks, MapPin, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { api, queryString } from "../../api/client";
import type {
  AnalyticsDashboard,
  Booking,
  BookingTask,
  Invoice,
  ListResponse,
} from "../../api/types";
import { useVenueScope } from "../../app/venueScope";
import { useAuth } from "../../auth/AuthProvider";
import { membershipAllows, membershipAllowsIn } from "../../auth/permissions";
import { formatMoney } from "../../lib/format";
import { ErrorPanel, PageHeader } from "../../ui/components";
import { DashboardSkeleton } from "../../ui/skeletons";
import type { AssetReservation, InventoryReservation } from "../inventory/inventoryTypes";
import {
  type HomeRecord,
  NextUpSection,
  queryCount,
  ReadinessSection,
  type ReadinessTile,
  timeGreeting,
  UpcomingSection,
} from "./homeSections";

const terminalStatuses = new Set(["completed", "cancelled", "rejected", "declined", "expired"]);

function toRecord(booking: Booking): HomeRecord {
  // A hire is scheduled as occurrences, each holding its own rooms, so the places it takes up are
  // gathered from all of them rather than read off the booking.
  const allocations = booking.occurrences?.flatMap((occurrence) => occurrence.allocations) ?? [];
  return {
    id: booking.id,
    reference: booking.reference,
    title: booking.title,
    status: booking.status,
    start: booking.start,
    timezone: booking.timezone,
    summary: booking.description,
    customerName: booking.customerName,
    attendeeCount: booking.attendeeCount,
    venueNames: [...new Set(allocations.map((allocation) => allocation.venueName))],
    locationNames: [...new Set(allocations.map((allocation) => allocation.locationName))],
    path: `/bookings/${booking.id}`,
  };
}

// Home for the lettings side: what somebody else has hired, and what is outstanding on it. The
// client's own programme has a home of its own, because the two are different trades over
// different money and a single page answering for both told neither story properly.
export function BookingsHomePage() {
  const auth = useAuth();
  const membership = auth.session?.selectedMembership;
  // Judged by the lettings role rather than across both, because everything on this page is a hire.
  // Somebody who keeps the books for the client's own events is not owed the hirers' pipeline.
  const canViewAnalytics = membershipAllowsIn(membership, "bookings", "analytics.view");
  const canViewTasks = membershipAllowsIn(membership, "bookings", "bookings.tasks.manage");
  const canViewFinance = membershipAllowsIn(membership, "bookings", "invoices.issue");
  // Stock is the client's own kit wherever it is going, so it is asked across both halves, which is
  // how the server judges the reservation routes too.
  const canViewInventory = membershipAllows(membership, "inventory.operate");
  const { selectedVenueId: venueId, loading: venueLoading } = useVenueScope();
  const today = startOfDay(new Date());
  const horizon = addDays(today, 365);
  const listQuery = queryString({ from: formatISO(today), to: formatISO(horizon), venueId });
  const yearFrom = startOfYear(today);
  const yearTo = addYears(yearFrom, 1);
  const pipelineQuery = queryString({ from: formatISO(yearFrom), to: formatISO(yearTo), venueId });

  const analytics = useQuery({
    queryKey: ["analytics", "home-pipeline", pipelineQuery],
    queryFn: () => api<AnalyticsDashboard>(`/api/v1/analytics/dashboard${pipelineQuery}`),
    enabled: canViewAnalytics && Boolean(venueId),
  });
  const bookings = useQuery({
    queryKey: ["bookings", "home-upcoming", listQuery],
    queryFn: () => api<ListResponse<Booking>>(`/api/v1/bookings${listQuery}`),
    enabled: Boolean(membership && venueId),
  });
  const upcoming = (bookings.data?.items ?? [])
    .filter((booking) => !terminalStatuses.has(booking.status))
    .sort((left, right) => new Date(left.start).getTime() - new Date(right.start).getTime())
    .map(toRecord);
  const next = upcoming[0];
  const nextID = next?.id ?? "";
  const tasks = useQuery({
    queryKey: ["bookings", nextID, "tasks"],
    queryFn: () => api<ListResponse<BookingTask>>(`/api/v1/bookings/${nextID}/tasks`),
    enabled: Boolean(nextID && canViewTasks),
  });
  const invoices = useQuery({
    queryKey: ["invoices", "home-next-booking", nextID],
    queryFn: () =>
      api<ListResponse<Invoice>>(
        `/api/v1/invoices?bookingId=${encodeURIComponent(nextID)}&limit=100&offset=0`,
      ),
    enabled: Boolean(nextID && canViewFinance),
  });
  const reservations = useQuery({
    queryKey: ["inventory", "reservations", nextID],
    queryFn: () =>
      api<ListResponse<InventoryReservation>>(`/api/v1/bookings/${nextID}/inventory-reservations`),
    enabled: Boolean(nextID && canViewInventory),
  });
  const assetReservations = useQuery({
    queryKey: ["inventory", "asset-reservations", nextID],
    queryFn: () =>
      api<ListResponse<AssetReservation>>(`/api/v1/bookings/${nextID}/asset-reservations`),
    enabled: Boolean(nextID && canViewInventory),
  });

  if (
    venueLoading ||
    (Boolean(venueId) && (bookings.isPending || (canViewAnalytics && analytics.isPending)))
  ) {
    return <DashboardSkeleton title={timeGreeting()} />;
  }
  if (bookings.error) return <ErrorPanel error={bookings.error} />;
  if (analytics.error && canViewAnalytics) return <ErrorPanel error={analytics.error} />;

  const firstName = auth.session?.user.displayName.split(" ")[0] ?? "there";
  const openTasks = (tasks.data?.items ?? []).filter(
    (task) => task.status !== "completed" && task.status !== "cancelled",
  ).length;
  const bookingInvoices = (invoices.data?.items ?? []).filter(
    (invoice) => invoice.bookingId === nextID,
  );
  const outstandingMinor = bookingInvoices.reduce(
    (total, invoice) => total + invoice.balanceMinor,
    0,
  );
  const reservedResources =
    (reservations.data?.items ?? []).filter((item) => item.status !== "cancelled").length +
    (assetReservations.data?.items ?? []).filter((item) => item.status !== "cancelled").length;

  const tiles: ReadinessTile[] = next
    ? [
        {
          key: "spaces",
          label: "Spaces",
          icon: <MapPin aria-hidden="true" />,
          value: next.locationNames.length || "Pending",
        },
        {
          key: "guests",
          label: "Expected guests",
          icon: <Users aria-hidden="true" />,
          value: next.attendeeCount ?? "Pending",
        },
        ...(canViewTasks
          ? [
              {
                key: "tasks",
                label: "Open tasks",
                icon: <ListChecks aria-hidden="true" />,
                value: queryCount(tasks.isLoading, tasks.error, openTasks),
                attention: true,
              },
            ]
          : []),
        ...(canViewFinance
          ? [
              {
                key: "balance",
                label: "Balance due",
                icon: <CircleDollarSign aria-hidden="true" />,
                value: invoices.isLoading
                  ? "Loading"
                  : invoices.error
                    ? "Unavailable"
                    : formatMoney(outstandingMinor, bookingInvoices[0]?.currency ?? "USD"),
                attention: outstandingMinor > 0,
              },
            ]
          : []),
        ...(canViewInventory
          ? [
              {
                key: "resources",
                label: "Reserved resources",
                icon: <Boxes aria-hidden="true" />,
                value: queryCount(
                  reservations.isLoading || assetReservations.isLoading,
                  reservations.error ?? assetReservations.error,
                  reservedResources,
                ),
              },
            ]
          : []),
      ]
    : [];

  return (
    <>
      <PageHeader
        title={`${timeGreeting()}, ${firstName}`}
        description="Your next rental comes first, followed by the work and commercial signals that need attention."
        actions={
          <Link className="button secondary" to="/calendar">
            Open calendar <ArrowRight size={17} />
          </Link>
        }
      />

      <NextUpSection
        record={next}
        labels={{
          eyebrow: "Booking command view",
          heading: "Next booking",
          openAria: "Open booking overview for",
          emptyTitle: "No upcoming bookings",
          emptyDescription:
            "Take a booking from the calendar to start coordinating the customer, venue, team, finance, and resources in one place.",
          emptyActionLabel: "Create booking",
          emptyActionTo: "/calendar/new",
        }}
      />

      {next ? (
        <ReadinessSection
          eyebrow="At a glance"
          heading="Booking readiness"
          briefTo={next.path}
          briefLabel="See the full booking brief"
          tiles={tiles}
        />
      ) : null}

      {analytics.data ? (
        <section aria-labelledby="home-pipeline-heading" className="home-section">
          <div className="section-heading-row">
            <div>
              <span className="eyebrow">Commercial pulse</span>
              <h2 id="home-pipeline-heading">Booking pipeline</h2>
              <p>Each booking is counted once. Completed covers this calendar year.</p>
            </div>
            <Link to="/analytics">Open analytics</Link>
          </div>
          <div className="pipeline-grid">
            {analytics.data.pipeline.map((stage) => (
              <article
                className={`pipeline-stage stage-${stage.key.replace("_", "-")}`}
                key={stage.key}
                title={stage.definition}
              >
                <span>{stage.key === "completed_ytd" ? "Completed YTD" : stage.label}</span>
                <strong>{stage.count}</strong>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <UpcomingSection
        records={upcoming.slice(1, 6)}
        title="Coming up after this"
        description="The next rentals at this venue."
        emptyTitle="Nothing else scheduled"
        emptyDescription="New bookings will appear here as they are qualified and scheduled."
      />
    </>
  );
}
